import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import {
    ColorMap,
    MutualFundTableBodyProps,
    SchemeInfoProps,
    TableBodyData,
    TableItemCheckboxProps
} from "../../types";
import {
    CheckIcon,
    GoldenStarFilled,
    UnCheckIcon
} from "../../../../assets/SvgIcons";
import { useState } from "react";
import { checkImageExists } from "../../../../utils/utilityFunctions";
import dayjs from "../../../../utils/dayjs";

const colorMap: ColorMap = {
    low: 'success.800',
    medium: 'warning.500',
    "very high": 'error.500'
}

function SchemeInfo({
    company,
    schemeName,
    type,
    subType,
    risk,
    isinCode,
    onSchemeClick = () => { return; }
}: SchemeInfoProps) {
    const amcIds = ['BDOTR', 'BPIAMC']
    const checkAmcId = amcIds.includes(company ?? '') ? company?.toLowerCase() : company;
    return (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: 'row', gap: '8px' }}>
            {company &&
                <img
                    src={checkImageExists(`${checkAmcId}`) ? `../../../../../../images/amcIcons/${checkAmcId}.svg` : `../../../../../../images/amcIcons/default.svg`}
                    alt={company}
                    width={50}
                    height={50}
                    style={{
                        objectFit: "contain",
                        borderRadius: "8px"
                    }}
                />
            }
            <Box
                role="button"
                tabIndex={0}
                aria-label={`View details for ${schemeName}`}
                sx={{
                    cursor: "pointer",
                    width: '100%',
                }}
                onClick={(event: React.MouseEvent) => onSchemeClick(event)}
                onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onSchemeClick(event);
                    }
                }}
            >
                <Tooltip title={schemeName} followCursor>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {schemeName}
                    </Typography>
                </Tooltip>
                <Grid
                    container
                    sx={{
                        marginTop: "10px",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        columnGap: "8px"
                    }}
                >
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="body_400"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                            })}
                        >
                            {type}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={{
                                width: "3px",
                                height: "3px",
                                backgroundColor: "text.secondary",
                                borderRadius: "50%",
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="body_400"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                                width: '100%',
                            })}
                        >
                            {subType}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={(theme: Theme) => ({
                                width: "3px",
                                height: "3px",
                                backgroundColor: theme.palette.textDark.primary,
                                borderRadius: "50%",
                                display: (!risk || risk === "NULL") ? "none" : "block",
                            })}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="body_400"
                            sx={(theme: Theme) => ({
                                color: theme.palette.textDark.primary,
                                display: (!risk || risk === "NULL") ? "none" : "block",
                            })}
                        >
                            {risk} Risk
                        </Typography>
                    </Grid>
                </Grid>
                <Box sx={{ marginTop: "3px" }}>
                    <Typography
                        variant="body_400"
                        color="text.primary"
                    >
                        ISIN:{' '}
                        <Typography
                            component="span"
                            variant="body_400"
                            color="text.secondary"
                        >
                            {isinCode ?? ""}
                        </Typography>
                    </Typography>
                </Box>
            </Box >
        </Box>
    )
}

function TableItemCheckbox({
    company,
    value,
    schemeName,
    type = "",
    subType = "",
    risk = "",
    isinCode = "",
    checked,
    onCheckboxClick,
    onSchemeClick = () => { return; }
}: TableItemCheckboxProps) {
    return (
        <FormControlLabel
            value={value}
            control={
                <Checkbox
                    size="small"
                    checkedIcon={<CheckIcon />}
                    icon={<UnCheckIcon />}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={onCheckboxClick}
                    checked={checked}
                    inputProps={{
                        'aria-label': `Select ${schemeName}`,
                        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                (event.currentTarget as HTMLInputElement).click();
                            }
                        }
                    } as React.InputHTMLAttributes<HTMLInputElement>}
                />
            }
            label={
                <SchemeInfo
                    company={company}
                    schemeName={schemeName}
                    type={type}
                    subType={subType}
                    risk={risk}
                    isinCode={isinCode ?? ""}
                    onSchemeClick={(event?: React.MouseEvent | React.KeyboardEvent) => {
                        if (event) {
                            event.stopPropagation();
                            event.preventDefault();
                        }
                        onSchemeClick();
                    }}
                />
            }
            sx={{
                display: 'flex',
                "& .MuiFormControlLabel-label": {
                    marginLeft: "15px",
                    width: "100%",
                }
            }}
            onClick={(event) => {
                event.preventDefault()
            }}
        />
    );
}


export default function MutualFundTableBody(
    {
        tableBodyData,
        tableBodyRowCustomStyles = {},
        showCheckbox = true,
        handleCheckboxClick = () => { return; },
        handleSchemeClick = () => { return; },
        selectedCategory = ""
    }: MutualFundTableBodyProps
): JSX.Element {
    const theme = useTheme()
    const isCategoryNFO = selectedCategory === "NFO";

    return (
        <>
            {tableBodyData.map((obj: TableBodyData) => (
                <TableRow
                    key={obj.scheme}
                    sx={{
                        '& td': {
                            paddingTop: "10px",
                            paddingBottom: "10px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        ...tableBodyRowCustomStyles
                    }}
                >
                    <TableCell
                        align="left"
                        sx={{
                            maxWidth: "300px",
                            overflow: "hidden",
                        }}
                    >
                        {showCheckbox ?
                            <TableItemCheckbox
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                value={obj?.scheme ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                isinCode={obj?.isinCode ?? ""}
                                checked={!!obj?.checked}
                                onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(obj?.scheme ?? "", event.target.checked)}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            /> : <SchemeInfo
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                isinCode={obj?.isinCode ?? ""}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            />}
                    </TableCell>
                    {!isCategoryNFO && (
                        <>
                            {/* <TableCell align="center">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                        borderRadius: '20px',
                                        padding: '12px',
                                        width: 'fit-content',
                                        mx: 'auto',
                                        gap: '4px'
                                    }}
                                >
                                    {
                                        obj?.fundRating?.trim().toLowerCase() === 'not rated'
                                            ?
                                            (
                                                <>
                                                    <Typography
                                                        variant="overline_400"
                                                        sx={(theme) => ({
                                                            color: theme.palette.text.primary,
                                                        })}
                                                    >
                                                        Not Rated
                                                    </Typography>
                                                </>
                                            )
                                            : obj?.fundRating && !Number.isNaN(Number(obj?.fundRating)) && Number(obj?.fundRating) > 0 ? (
                                                <>
                                                    <Typography
                                                        variant="overline_400"
                                                        sx={(theme) => ({
                                                            color: theme.palette.text.primary,
                                                            mt: 0.3
                                                        })}
                                                    >
                                                        {Number(Math.floor(obj?.fundRating))}
                                                    </Typography>
                                                    <GoldenStarFilled />
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="overline_400"
                                                        sx={(theme) => ({
                                                            color: theme.palette.text.primary,

                                                        })}
                                                    >
                                                        Not Rated
                                                    </Typography>

                                                </>
                                            )
                                    }
                                </Box>
                            </TableCell> */}

                            <TableCell align="center">
                                <Typography
                                    variant="body_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.year1Return && !Number.isNaN(Number(obj.year1Return)) ? `${Number(obj.year1Return).toFixed(2)}%` : "0%"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.year3Return && !Number.isNaN(Number(obj.year3Return)) ? `${Number(obj.year3Return).toFixed(2)}%` : "0%"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.year5Return && !Number.isNaN(Number(obj.year5Return)) ? `${Number(obj.year5Return).toFixed(2)}%` : "0%"}
                                </Typography>
                            </TableCell>
                        </>
                    )}
                    {
                        isCategoryNFO && (
                            <>
                                <TableCell align="center">
                                    <Typography
                                        variant="body_400"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {obj?.issueOpen ? dayjs(obj?.issueOpen).format("DD/MM/YYYY") : "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography
                                        variant="body_400"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {obj?.issueStatedClose ? dayjs(obj?.issueStatedClose).format("DD/MM/YYYY") : "-"}
                                    </Typography>
                                </TableCell>
                            </>
                        )
                    }
                </TableRow>
            ))}
        </>
    )
}

export function MutualFundResponsiveBody({
    tableBodyData,
    tableBodyRowCustomStyles = {},
    showCheckbox = true,
    handleCheckboxClick = () => { return; },
    handleSchemeClick = () => { return; },
    tableHeaderData = [],
}: MutualFundTableBodyProps): JSX.Element {

    const { palette } = useTheme()
    const [tableHeaderKey, setTableHeaderKey] = useState<string>(tableHeaderData[1].key || tableHeaderData[1].id)

    const style: any = { color: palette.text.secondary, fontWeight: 500, fontSize: '14px', textTransform: 'uppercase', textWrap: 'nowrap', paddingRight: 0, paddingLeft: 0 }
    return (
        <TableBody>
            {tableBodyData.map((item: any, index: number) => (
                <TableRow key={index}>
                    <TableCell
                        style={{ ...style }}
                        align='left'
                    >
                        <Typography
                            variant="subtitle_500"
                            sx={(theme: Theme) => ({
                                // color: theme.palette.primary[500],
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '200px',
                                width: '100%',
                            })}
                        >
                            {showCheckbox ?
                                <TableItemCheckbox
                                    schemeName={item?.schemeName ?? ""}
                                    value={item?.scheme ?? ""}
                                    type={item?.categoryName ?? ""}
                                    subType={item?.subCategoryName ?? ""}
                                    risk={item?.riskType ?? ""}
                                    isinCode={item?.isinCode ?? ""}
                                    checked={!!item?.checked}
                                    onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(item?.scheme ?? "", event.target.checked)}
                                    onSchemeClick={() => handleSchemeClick(item)}
                                /> : <SchemeInfo
                                    company={item?.company ?? ""}
                                    schemeName={item?.schemeName ?? ""}
                                    type={item?.categoryName ?? ""}
                                    subType={item?.subCategoryName ?? ""}
                                    risk={item?.riskType ?? ""}
                                    isinCode={item?.isinCode ?? ""}
                                    onSchemeClick={() => handleSchemeClick(item)}
                                />}
                            {/* {item?.[tableHeaderData[0].id]} */}
                        </Typography>
                    </TableCell>
                    <TableCell
                        style={{ ...style }}
                        align='center'
                    >
                        <Typography
                            variant="subtitle_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                            })}
                        >
                            {tableHeaderKey.includes('.') ? item?.[tableHeaderKey.split('.')[0]][tableHeaderKey.split('.')[1]] : item?.[tableHeaderKey]}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    )
}